import { useContext, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";

import { Badge } from "@magnetic/badge";
import { Container } from "@magnetic/container";
import { Flex } from "@magnetic/flex";
import { Icon, Nav, NavIconsKind } from "@magnetic/nav";
import { Text } from "@magnetic/text";

import { UserRole } from "src/gen/schema/models/auth";
import { useInvalidEndpointSummary } from "src/hooks/useInvalidEndpointCount";
import { useOrgModal } from "src/pages/org/orgUtils";

import { PATHS } from "./constants";
import { LoginContext } from "./loginContext";
import { OrgsSwitcherDrawer, OrgSwitcher } from "./orgSwitcher";

import "./navbar.scss";

type navLabels =
  // "Overview",
  | "Fabrics"
  | "Switches"
  | "Inventory"
  | "Connectivity"
  | "Certificates"
  | "Root certificates"
  | "Server certificates"
  | "Insights"
  | "Devices"
  | "Pluggables"
  | "Fans"
  | "Switch MAC addresses"
  | "Cloud connectivity"
  | "Recent changes"
  | "Reports"
  | "Administration"
  | "Organization settings"
  | "Power supply units"
  | "Organization management"
  | "API tokens"
  | "ChatBot settings"
  | "Audit logs"
  | "Notifications"
  | "Multi-Site"
  | "User management"
  | "Subscriptions";

export const navUrls = [
  // "/overview",
  "/fabrics",
  "/switches",
  "/inventory",
  "/connectivity",
  "/insights",
  "/devices",
  "/pluggables",
  "/fans",
  "/switch-mac-addresses",
  "/cloud-connectivity",
  "/recent-changes",
  "/reports",
  "/administration",
  "/power-supply-units",
  "/user-management",
  "/root-certificates",
  "/server-certificates",
  "/organization-settings",
  "/api-tokens",
  "/chatbot-settings",
  "/multisite",
  "/audit-logs"
] as const;

type LabelType = navLabels | "Developer" | "User and bot management";
type URLType =
  | (typeof navUrls)[number]
  | "/dev/settings"
  | "/notifications/policies"
  | "/subscription/management";

type SubMenuItem = {
  label?: LabelType;
  to?: URLType;
  matchPaths?: string[];
  /**
   * This will enable/disable the menu item
   * based on the feature toggle in the
   * LocalSettings. Default is enabled = true.
   */
  enabled?: boolean;
  subHeading?: string;
};

type NavConfigItem = {
  label?: LabelType;
  kind?: NavIconsKind;
  subMenu?: SubMenuItem[];
  to?: URLType;
  matchPaths?: string[];
  role?: UserRole;
  dividerKey?: string;
  enabled?: boolean;
};

type NavSubMenuConfigItem = {
  label: LabelType;
  kind?: NavIconsKind;
  subMenu: SubMenuItem[];
  to: URLType;
  dividerKey?: string;
};

type NavConfigType = (NavConfigItem | NavSubMenuConfigItem)[];

const getNavConfig = (): NavConfigType => [
  {
    label: "Multi-Site",
    kind: "global-overview",
    to: "/multisite"
  },
  {
    label: "Fabrics",
    to: "/fabrics",
    matchPaths: [
      "/fabrics",
      `/${PATHS.FABRIC}/`,
      `/${PATHS.FABRIC_TEMPLATES}/`,
      `/${PATHS.FABRIC_DESIGNER}/`
    ]
  },
  {
    label: "Inventory",
    to: "/devices",
    matchPaths: [
      "/devices",
      `/${PATHS.INVENTORY}/`,
      `/${PATHS.INVENTORY_COUNTS}`
    ]
  },
  {
    label: "Subscriptions",
    to: "/subscription/management",
    kind: "licensing",
    matchPaths: [`/${PATHS.SUBSCRIPTION}/`]
  },
  {
    dividerKey: "div1"
  },
  {
    label: "Administration",
    kind: "admin",
    role: UserRole.ADMIN,
    subMenu: [
      {
        label: "User and bot management",
        to: "/user-management",
        matchPaths: [`/${PATHS.USER_MGMT}`, `/${PATHS.BOT_MGMT}`]
      },
      {
        label: "Root certificates",
        to: "/root-certificates"
      },
      {
        label: "Server certificates",
        to: "/server-certificates"
      },
      {
        label: "Organization settings",
        to: "/organization-settings",
        enabled: window.cnc.settings.get("optInC3")
      }
    ]
  },
  {
    label: "Notifications",
    kind: "alerts",
    to: "/notifications/policies",
    matchPaths: [`/${PATHS.NOTIFICATIONS}/`]
  },
  {
    label: "Developer",
    kind: "settings",
    to: "/dev/settings",
    enabled: window.cnc.settings.get("dev"),
    matchPaths: [`/${PATHS.DEV}/`]
  }
];

export const Navbar = () => {
  const { pathname } = useLocation();
  const { session } = useContext(LoginContext);
  const { openOrgModal, orgModalContent } = useOrgModal();
  const { counts, error: endpointFetchError } = useInvalidEndpointSummary();
  const userRole = session?.role;
  const showOrgSwitcher = !session?.isC3Crosslaunch;

  const selected = useMemo((): URLType | undefined => {
    const isPathMatch = (
      paths: string[] | undefined,
      fallbackPath?: string
    ) => {
      const pathList = paths ?? (fallbackPath ? [fallbackPath] : []);
      return pathList.some((prefix) => pathname.startsWith(prefix));
    };

    const navConfig = getNavConfig();
    for (const item of navConfig) {
      const { to, subMenu, matchPaths } = item as NavSubMenuConfigItem &
        NavConfigItem;
      if (subMenu) {
        const match = subMenu.find(
          ({ to: url, matchPaths: subMenuMatchPaths }) =>
            isPathMatch(subMenuMatchPaths, url)
        );
        if (match?.to) return match.to;
      }
      if (to && isPathMatch(matchPaths, to)) return to;
    }
    return undefined;
  }, [pathname]);

  const updatedNavConfig = useMemo(() => {
    const cfg = getNavConfig();
    /**
     * TODO: Add proper Role permissions
     * TORT-1661 [UI]: Block non-admin users from accessing User management page
     *
     * This is another Alpha hack to hide
     * the administration menu item from
     * users that do not have Admin role.
     *
     * !!! This should be considered total throw away code!!!
     */
    return cfg.filter(({ role, enabled = true }: NavConfigItem) => {
      if (role) {
        // most be both enabled and have a matching role
        return enabled && role === userRole;
      }
      // if no role is specified, then just check enabled
      return enabled;
    });
  }, [userRole]);

  const nav = updatedNavConfig.map((itemConf) => {
    if (itemConf.dividerKey) {
      return <Nav.Divider key={itemConf.dividerKey} />;
    }
    const { kind, label, subMenu, to } = itemConf as NavSubMenuConfigItem;

    // If there are endpoint with status down, or an error in fetching the endpoints,
    // add a red dot to the "Notifications" icon
    const conf = {
      icon:
        label === "Notifications" &&
        (counts.total > 0 || endpointFetchError) ? (
          <Badge.Dot>
            <Icon kind={kind ?? (label.toLowerCase() as NavIconsKind)} />
          </Badge.Dot>
        ) : (
          <Icon kind={kind ?? (label.toLowerCase() as NavIconsKind)} />
        ),
      label: label
    };
    if (subMenu) {
      return (
        <Nav.Item
          {...conf}
          key={label}
          drawerContentId={label.toLowerCase()}
          isSelected={subMenu.some(({ to: url }) => url === selected)}
        />
      );
    }
    return (
      <Link to={to} key={label}>
        <Nav.Item {...conf} isSelected={selected === to} />
      </Link>
    );
  });

  const getSubNavItems = (subMenu?: SubMenuItem[]) =>
    subMenu
      ?.filter(({ enabled = true }) => enabled)
      ?.map(({ label, to, subHeading }) => {
        if (subHeading && !to) {
          return (
            <Text key={subHeading} className="sub-menu-heading">
              {subHeading}
            </Text>
          );
        } else if (!!to) {
          return (
            <Link to={to} key={label}>
              <Nav.Drawer.Item selected={selected === to}>
                {label}
              </Nav.Drawer.Item>
            </Link>
          );
        } else {
          return null;
        }
      });

  const subNav = updatedNavConfig
    .filter(({ subMenu }) => subMenu)
    .map((item) => {
      const { label, subMenu } = item as NavSubMenuConfigItem;
      return (
        <Nav.Drawer.Content
          heading={label}
          id={label?.toLowerCase()}
          key={label}
        >
          {getSubNavItems(subMenu)}
        </Nav.Drawer.Content>
      );
    });

  return (
    <Container>
      <Flex>
        <Nav
          className="sticky-nav"
          initialIsCollapsed={true}
          isIconOnly={true}
          key="nav"
          navZIndex={1}
        >
          <Nav.Collapse auto={false} />
          <Nav.Menu>
            {showOrgSwitcher && <OrgSwitcher />}
            {nav}
          </Nav.Menu>
          <Nav.Drawer key="nav-drawer">
            {showOrgSwitcher && (
              <OrgsSwitcherDrawer openOrgModal={openOrgModal} key="orgs" />
            )}
            {subNav}
          </Nav.Drawer>
        </Nav>
      </Flex>
      {orgModalContent}
    </Container>
  );
};
